/**
 * NotificationCenterPage — 알림 센터 / 인박스 (화면정의서 SC-022 · 기능명세 FN-068).
 *
 * 받은 알림을 모아 보고, 탭하면 대상으로 가면서 읽음 처리된다. 진입은 홈 상단 벨.
 *
 * ── 2026-08-25 명세대로 다시 짬 ──────────────────────────────────────
 * 전에는 템플릿(픽쿠리) 화면이었다 — 스와이프 탭 4개(라벨만 peeka), 절대 날짜,
 * '알림이 없습니다.', 한국어 하드코딩. 화면정의서 rev10 이 SC-022 를 새로
 * 정의해 그대로 옮겼다:
 *
 *   헤더    '알림' + **전체 읽음**
 *   배너    OS 알림이 꺼져 있으면 설정 유도 (인앱 알림은 계속 온다)
 *   필터    유형 칩 — 전체 · 시세 · 컬렉션 · 발견 · 공지  (명세상 '선택')
 *   행      유형 아이콘 · 제목 · 본문 · **상대 시각** · 안읽음 점
 *   빈 상태 '아직 알림이 없어요'
 *
 * 탭이 아니라 칩인 이유: 탭은 '다른 화면들' 을 뜻해 안 고르면 못 보는 것이
 * 생긴다. 여기서 기본값은 **전부 보기** 이고 유형은 좁히는 수단이라 칩이 맞다.
 * 전에는 '전체' 가 없어 알림 하나를 보려면 어느 탭인지 먼저 맞혀야 했다.
 *
 * ⚠ 우상단 **설정(→ SC-024)** 은 아직 없다. 알림 설정 화면이 라우트에 없어
 *   갈 곳이 없다 — 아무 데도 안 가는 톱니가 없는 것보다 나쁘다.
 * ⚠ **비회원 로그인 유도** 도 아직이다. 이 라우트가 RequireAuth 로 감싸여 있어
 *   비회원은 화면에 닿기 전에 튕긴다. 라우팅은 개발 영역이라 남겨 둔다.
 *
 * 데이터는 react-query(useNotificationsQuery)가 source of truth.
 * ⚠ 서버가 빈 배열을 주면 더미로 채운다 — 알림 API 가 아직 안 붙었다.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@astryxdesign/core/Badge";
import { Button } from "@astryxdesign/core/Button";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Text } from "@astryxdesign/core/Text";
import { HStack, VStack } from "@astryxdesign/core/Layout";
import { ToggleButton, ToggleButtonGroup } from "@astryxdesign/core/ToggleButton";
import {
  markAllRead,
  markNotificationRead,
  type NotificationItem,
} from "../../api/notification";
import {
  useNotificationsQuery,
  NOTIFICATIONS_KEY,
  NOTIFICATIONS_COUNT_KEY,
} from "../../hooks/useNotificationQueries";
import { useOverlayNavigate } from "../../hooks/useOverlayNavigate";
import { buildDummyNotifications } from "../../data/dummyNotifications";
import { getPushPermissionState, openPushSettings } from "../../native/pushPermission";
import Icon from "../../components/primitives/Icon";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { useAppBarSlot } from "../../components/layout/AppBarSlotContext";
import {
  NOTIFICATION_TYPES,
  hasDetailView,
  iconForCode,
  isTargetGone,
  matchesType,
  relativeTimeKey,
  resolveNotificationPath,
  type NotificationTypeKey,
} from "./notificationMeta";
import styles from "./NotificationCenterPage.module.css";
import Notice from "../../components/feedback/Notice";
import SwipeTabPanel from "../../components/primitives/SwipeTabPanel";

type Filter = NotificationTypeKey | "all";
const FILTER_TABS: readonly Filter[] = ["all", ...NOTIFICATION_TYPES];

export default function NotificationCenterPage() {
  const { t } = useTranslation("notification");
  const queryClient = useQueryClient();
  const overlayNavigate = useOverlayNavigate();
  const { data, isLoading, isError, refetch } = useNotificationsQuery();
  const [filter, setFilter] = useState<Filter>("all");

  /* OS 알림 권한 — 꺼져 있으면 배너로 설정을 권한다 (화면정의서 SC-022).
     ⚠ 인앱 알림은 계속 온다. 이 배너는 '푸시가 안 온다' 는 말이지
       '알림이 없다' 는 말이 아니다 — 문구가 그렇게 읽히면 안 된다.
     ⚠ 웹에서는 unsupported 라 뜨지 않는다. 실기기에서만 보인다. */
  const [pushDenied, setPushDenied] = useState(false);
  useEffect(() => {
    void getPushPermissionState().then((state) => setPushDenied(state === "denied"));
  }, []);

  /**
   * ── dev 더미 (붙으면 이 블록과 usingDummy 분기만 지운다) ──────────────
   *
   * ⚠ **dev 서버에서만** 채운다. 알림 API 가 아직 없어 화면이 늘 비어 있으면
   *   만들 수가 없다. MODE 로 가르는 이유: DEV 플래그는 테스트에서도 참이라
   *   '빈 상태' 를 영영 못 보게 된다. 여기서 원하는 건 개발 서버 하나뿐이다
   *   (development = npm run dev · test = vitest · production = 배포).
   *
   * ⚠ 화면에서 갖고 있지 않고 **쿼리 캐시에 심는다.** 처음엔 memo 로 들었더니
   *   '전체 읽음' 이 아무 일도 하지 않았다 — 읽음 처리는 캐시를 고치는데
   *   화면이 보는 건 캐시 밖의 더미였다. 캐시에 심으면 읽음·이동 경로가
   *   서버가 붙은 뒤와 똑같이 돈다.
   */
  const usingDummy = useRef(false);
  useEffect(() => {
    if (import.meta.env.MODE !== "development") return;
    if (isLoading || (data ?? []).length > 0) return;
    usingDummy.current = true;
    queryClient.setQueryData<NotificationItem[]>(NOTIFICATIONS_KEY, buildDummyNotifications());
  }, [data, isLoading, queryClient]);

  const items: NotificationItem[] = data ?? [];

  const visible = items.filter((n) => matchesType(n.category, filter));
  const unreadCount = items.filter((n) => n.readYn === "N").length;

  /** 전체 읽음 (FN-068) — 안읽음 배지를 0 으로 만든다 */
  const handleReadAll = useCallback(async () => {
    queryClient.setQueryData<NotificationItem[]>(NOTIFICATIONS_KEY, (prev) =>
      prev?.map((i) => ({ ...i, readYn: "Y" as const })),
    );
    queryClient.setQueryData<boolean>(NOTIFICATIONS_COUNT_KEY, false);
    if (usingDummy.current) return; // dev 더미 — 보낼 서버가 없다
    try {
      await markAllRead();
    } catch {
      await queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    }
  }, [queryClient]);

  /* 앱바 우측 '전체 읽음' — 읽을 것이 없으면 내보내지 않는다.
     ⚠ 노드를 memo 하지 않으면 앱바가 매 렌더 갈아 끼운다.
     ⚠ ghost 버튼은 제 좌우 패딩(18px)을 들고 있어서 앱바 거터(24px) 위에 그냥
       얹으면 **글자가 42px 안쪽**에 선다. 왼쪽 뒤로가기 아이콘은 28px 이라
       좌우가 눈에 띄게 어긋났다. 상자를 그만큼 밖으로 빼 글자를 맞춘다. */
  const appBarAction = useMemo(
    () =>
      unreadCount > 0 ? (
        <span className={styles.appBarAction}>
          <Button
            variant="ghost"
            size="sm"
            label={t("readAll")}
            clickAction={() => void handleReadAll()}
          />
        </span>
      ) : undefined,
    [unreadCount, t, handleReadAll],
  );
  /* ⚠ **이 화면이 보일 때만** 앱바를 차지한다 (2026-08-27 버그 수정).
     알림에서 홈으로 돌아가도 '전체 읽음' 이 앱바에 남아 있었다. 이 화면은
     액티비티 스택 위에 얹히는데, 뒤로 나간 뒤에도 잠시 마운트된 채 남고
     그동안 안읽음 수가 바뀌면 effect 가 다시 돌아 **홈 앱바를 다시 차지했다.**
     경로를 함께 보면 남은 화면은 아무것도 claim 하지 못한다. */
  const { pathname } = useLocation();
  const isVisible = pathname.startsWith("/notifications");
  useAppBarSlot(undefined, isVisible ? appBarAction : undefined);

  const handleClick = async (item: NotificationItem) => {
    if (isTargetGone(item.category, item.deepLink)) return;

    // optimistic — UI 즉시 읽음 처리
    if (item.readYn === "N") {
      const nextList = queryClient.setQueryData<NotificationItem[]>(NOTIFICATIONS_KEY, (prev) =>
        prev?.map((i) =>
          i.notificationSerialNo === item.notificationSerialNo ? { ...i, readYn: "Y" } : i,
        ) ?? prev,
      );
      // 뱃지는 유무(dot)만 → 갱신된 리스트로 남은 unread 존재 여부 재계산.
      if (nextList) {
        queryClient.setQueryData<boolean>(
          NOTIFICATIONS_COUNT_KEY,
          nextList.some((i) => i.readYn === "N"),
        );
      }
      if (!usingDummy.current) {
        try {
          await markNotificationRead(item.notificationSerialNo);
        } catch {
          // prefix 매칭 → 리스트 + 카운트 동시 재동기화 (optimistic 롤백)
          await queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
        }
      }
    }

    if (item.deepLink) {
      overlayNavigate(resolveNotificationPath(item.category, item.deepLink));
      return;
    }
    if (hasDetailView(item.category)) {
      overlayNavigate(`/notifications/${item.notificationSerialNo}`);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.page}>
        <VStack gap={3} className={styles.loading}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} width="100%" height={72} />
          ))}
        </VStack>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.page}>
        <EmptyState
          title={t("errorTitle")}
          description={t("errorBody")}
          actions={
            <Button variant="secondary" label={t("retry")} clickAction={() => void refetch()} />
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {pushDenied && (
        <div className={styles.banner}>
          <Notice
            status="warning"
            title={t("pushOffTitle")}
            description={t("pushOffBody")}
            action={
              <Button
                variant="secondary"
                size="sm"
                label={t("pushOffAction")}
                clickAction={() => void openPushSettings()}
              />
            }
          />
        </div>
      )}

      {/* 유형 필터 — 가로로 넘치면 밀어서 본다. 좁은 기기에서 5개가 다 안 들어간다 */}
      <div className={styles.filters}>
        <ToggleButtonGroup
          type="single"
          value={filter}
          onChange={(v: string | null) => setFilter((v as Filter) ?? "all")}
          size="sm"
          label={t("filterLabel")}
        >
          <ToggleButton value="all" label={t("type.all")} />
          {NOTIFICATION_TYPES.map((type) => (
            <ToggleButton key={type} value={type} label={t(`type.${type}`)} />
          ))}
        </ToggleButtonGroup>
      </div>

      {/* 목록을 좌우로 밀면 유형 필터가 넘어간다. 필터 줄은 가로 스크롤이라 감싸지 않는다 */}
      <SwipeTabPanel tabs={FILTER_TABS} activeTab={filter} onTabChange={setFilter}>
      {visible.length === 0 ? (
        <EmptyState
          title={t("emptyTitle")}
          description={filter === "all" ? t("emptyBody") : t("emptyFiltered")}
        />
      ) : (
        <>
          <ul className={styles.list}>
            {visible.map((n) => {
              const isUnread = n.readYn === "N";
              const gone = isTargetGone(n.category, n.deepLink);
              const rel = relativeTimeKey(n.createdAt);
              return (
                <li key={n.notificationSerialNo}>
                  <button
                    type="button"
                    className={styles.row}
                    data-unread={isUnread}
                    onClick={() => void handleClick(n)}
                    disabled={gone}
                  >
                    {/* ⚠ 색은 prop 으로 준다. Icon 이 color 를 **인라인 style** 로
                        박아서 CSS 로는 못 이긴다 (DESIGNER §3.2 ③).
                        안읽음은 브랜드색으로 서고 읽으면 보조색으로 물러난다 —
                        오른쪽 점 하나로는 훑을 때 잘 안 잡힌다. */}
                    <Icon
                      name={iconForCode(n.category)}
                      size="sm"
                      color={isUnread ? "var(--color-text-accent)" : "var(--text-secondary)"}
                      className={styles.typeIcon}
                    />

                    <VStack gap={0.5} align="start" className={styles.rowBody}>
                      <HStack gap={1} align="center" wrap="wrap">
                        <Text weight="bold">{n.title}</Text>
                        {gone && <Badge label={t("targetGone")} />}
                      </HStack>
                      <Text type="supporting" color="secondary">{n.body}</Text>
                      <Text type="supporting" color="secondary">
                        {t(rel.key, { count: rel.count, date: rel.date })}
                      </Text>
                    </VStack>

                    {/* 안읽음 점 — 읽으면 자리만 남는다(글이 밀리지 않게) */}
                    <span
                      className={styles.dot}
                      aria-label={isUnread ? t("unread") : undefined}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
          <p className={styles.footnote}>{t("retention")}</p>
        </>
      )}
      </SwipeTabPanel>
    </div>
  );
}
